/**
 * TvAgenda — the room's upcoming-meetings list on the TV idle screen (Zoom-Rooms
 * style "what's next in this room"). Pure presentation: events are a prop, fed by
 * {@link TvApp} from roomkeeper's `GET /rooms/:id/calendar`. Container-query sized
 * (cqw/cqh) like the rest of the TV so it scales identically on a wall display and
 * an iPad preview.
 *
 * Renders the contract {@link CalendarEvent}. ALL-DAY events (sync8 emits a bare
 * `YYYY-MM-DD` start/end + `allDay: true`) show "All day" instead of a clock time —
 * we never coerce a bare date to a wall-clock instant.
 */

import type { CalendarEvent } from '@spaces/contracts';

/** How many upcoming rows the idle agenda shows before it would overflow the card. */
const MAX_ROWS = 4;

/**
 * The time label for an agenda row: "All day" for an all-day event, else the local
 * start time (12h, matching the idle clock). Falls back to the raw value if the
 * start can't be parsed (never throws on bad provider data).
 */
export function agendaTimeLabel(event: CalendarEvent): string {
    if (event.allDay) {
        return 'All day';
    }

    const ms = Date.parse(event.start);

    if (!Number.isFinite(ms)) {
        return event.start;
    }

    return new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/** A short day tag ("Today" / "Mon") so multi-day agendas read clearly. */
export function agendaDayLabel(event: CalendarEvent, now: number): string {
    const ms = Date.parse(event.start);

    if (!Number.isFinite(ms)) {
        return '';
    }

    const start = new Date(ms);
    const today = new Date(now);

    if (start.getFullYear() === today.getFullYear()
        && start.getMonth() === today.getMonth()
        && start.getDate() === today.getDate()) {
        return 'Today';
    }

    return start.toLocaleDateString([], { weekday: 'short' });
}

export interface TvAgendaProps {
    /** The room's upcoming events (already windowed/sorted by roomkeeper). */
    events: CalendarEvent[];
    /** "now" for the Today/day tagging — passed so the component stays pure. */
    now: number;
}

export function TvAgenda({ events, now }: TvAgendaProps): JSX.Element {
    const rows = events.slice(0, MAX_ROWS);

    return (
        <div className = "agenda" data-testid = "tv-agenda">
            <div className = "agenda__label">UP NEXT IN THIS ROOM</div>

            { rows.length === 0 ? (
                <div className = "agenda__empty" data-testid = "tv-agenda-empty">No meetings scheduled</div>
            ) : (
                <ul className = "agenda__list">
                    { rows.map(event => (
                        <li className = "agenda__item" key = { event.id } data-testid = "tv-agenda-item">
                            <span className = "agenda__time">
                                <span className = "agenda__day">{ agendaDayLabel(event, now) }</span>
                                <span className = "agenda__clock">{ agendaTimeLabel(event) }</span>
                            </span>
                            <span className = "agenda__title">{ event.title }</span>
                        </li>
                    )) }
                </ul>
            ) }
        </div>
    );
}
